import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import './styles/global.css'
import App from './App.tsx'
import styles from './App.module.css'

declare global {
  interface Window {
    __EVENT_ID__?: number
    // Set by events/displayEvent.cfm alongside __EVENT_ID__ - environment mirrors
    // application.environment ("dev"/"test"/"prod"), release is the same build-mtime
    // assetVersion string already used to cache-bust eventpage.js/css, reused here so a Sentry
    // event can be tied back to the exact deployed bundle.
    __RCS_ENV__?: string
    __RCS_RELEASE__?: string
  }
}

// DSN is a public, write-only identifier (Sentry's own docs call it out as safe to ship in
// client code - unlike request.stripeSecretKey/request.gatedropApiKey etc., which really are
// secrets and stay in the keystore table per CLAUDE.md's Security Checklist). Project: RCSignup,
// org: RogueAutomations.
Sentry.init({
  dsn: 'https://06500ea457fae057a84baa7767eb04f4@o4511344118398976.ingest.us.sentry.io/4512189328064512',
  environment: window.__RCS_ENV__ || 'unknown',
  release: window.__RCS_RELEASE__ ? `event-page@${window.__RCS_RELEASE__}` : undefined,
  // A render crash or an unhandled promise rejection anywhere on the page reaches Sentry's
  // default GlobalHandlers integration automatically - no extra window.onerror/unhandledrejection
  // wiring needed beyond this init() call.
})

/** Error boundary fallback - same "Couldn't load this event" look as App.tsx's own fetch-failure
 *  screen (styles.stateScreen), so a render crash and a data-fetch failure read as the same kind
 *  of error to a racer, not two different broken-page experiences. */
function ErrorFallback() {
  return (
    <div className={styles.stateScreen}>
      <p>Something went wrong loading this page.</p>
      <a href="/events/index.cfm">Back to events</a>
    </div>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <App eventId={window.__EVENT_ID__ ?? 0} />
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
